import { alpha, createTheme } from '@mui/material/styles';

const palettes = {
  light: {
    appBg: '#f6f8f8',
    surface: '#ffffff',
    surfaceElevated: '#ffffff',
    surfaceMuted: '#f1f5f4',
    border: '#dfe8e6',
    borderStrong: '#c7d7d4',
    textPrimary: '#182321',
    textSecondary: '#52615e',
    textMuted: '#7b8986',
    primary: '#229987',
    primaryHover: '#177d6e',
    primarySoft: '#e4f6f3',
    danger: '#dd5144',
    dangerHover: '#c7352a',
    warning: '#d97706',
    focus: 'rgba(34, 153, 135, 0.26)',
    shadow: '0 16px 45px rgba(17, 24, 39, 0.08)',
    shadowSoft: '0 8px 24px rgba(17, 24, 39, 0.06)',
  },
  dark: {
    appBg: '#0d1110',
    surface: '#0d1110',
    surfaceElevated: '#0d1110',
    surfaceMuted: '#121716',
    border: '#273331',
    borderStrong: '#33433f',
    textPrimary: '#f3faf8',
    textSecondary: '#bdcbc8',
    textMuted: '#859591',
    primary: '#2f8f83',
    primaryHover: '#3aa397',
    primarySoft: 'rgba(47, 143, 131, 0.16)',
    danger: '#ff7566',
    dangerHover: '#ff8d82',
    warning: '#fbbf24',
    focus: 'rgba(91, 211, 195, 0.28)',
    shadow: '0 18px 60px rgba(0, 0, 0, 0.5)',
    shadowSoft: '0 10px 32px rgba(0, 0, 0, 0.32)',
  },
};

export const createAppTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  const app = palettes[mode] || palettes.light;

  return createTheme({
    typography: {
      htmlFontSize: 14,
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      button: {
        textTransform: 'none',
      },
    },

    breakpoints: {
      values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
    },

    components: {
      MuiTableCell: {
        styleOverrides: {
          stickyHeader: {
            top: 0,
            left: 'auto',
            backgroundColor: app.surfaceElevated,
            color: app.textSecondary,
            fontWeight: 650,
            letterSpacing: 0,
          },
          root: {
            borderBottom: `1px solid ${app.border}`,
            color: app.textPrimary,
            padding: '0.75rem 1.25rem',
            maxWidth: '13rem',
            minWidth: '10rem',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: app.surface,
            fontSize: '14px',
            borderBottom: `1px solid ${app.border}`,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: { letterSpacing: 0 },
          body1: { fontSize: '14px', wordBreak: 'break-word', color: app.textPrimary },
          body2: { color: app.textSecondary },
          h2: { fontWeight: 720, fontSize: '35px', color: app.textPrimary },
          h3: { fontWeight: 680, fontSize: '30px', color: app.textPrimary },
          h6: { fontWeight: 600, fontSize: '14px', color: app.textPrimary },
          h4: { fontWeight: 650, fontSize: '20px', color: app.textPrimary },
          h5: { fontWeight: 600, fontSize: '20px', color: app.textPrimary },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: app.textSecondary,
            '&.Mui-checked': {
              color: app.primary,
            },
            '&.Mui-disabled': {
              color: alpha(app.textMuted, isDark ? 0.55 : 0.45),
            },
          },
          colorSecondary: {
            '&.Mui-checked': { color: app.primary },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: app.textSecondary,
            '&.Mui-checked': {
              color: app.primary,
            },
            '&.MuiCheckbox-indeterminate': {
              color: app.primary,
            },
            '&.Mui-disabled': {
              color: alpha(app.textMuted, isDark ? 0.55 : 0.45),
            },
          },
          colorSecondary: {
            '&.Mui-checked': { color: app.primary },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardInfo: {
            color: 'rgb(13, 60, 97)',
            backgroundColor: 'rgb(232, 244, 253)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            color: app.textPrimary,
            backgroundColor: app.surfaceMuted,
            border: `1px solid ${app.border}`,
            borderRadius: 8,
            fontWeight: 550,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            paddingLeft: '10px',
            paddingRight: 0,
            paddingBottom: '5px',
            '&::placeholder': {
              color: app.textSecondary,
              opacity: 0.85,
            },
          },
          root: {
            '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1px solid',
              borderColor: app.primary,
              boxShadow: `0 0 0 3px ${app.focus}`,
            },
            '&.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
              border: '1px solid',
              borderColor: app.danger,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              backgroundColor: app.surface,
              borderRadius: 10,
              color: app.textPrimary,
              transition:
                'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
            },
            '& .MuiInputLabel-root': {
              color: app.textSecondary,
              fontWeight: 500,
            },
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                border: '1px solid',
                borderColor: app.primary,
              },
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            minWidth: '50px !important',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: app.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: app.primary,
            },
          },
          notchedOutline: {
            borderColor: app.borderStrong,
          },
          input: {
            '&:-webkit-autofill': {
              boxShadow: 'none !important',
              textFillColor: 'black !important',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&:focus': {
              backgroundColor: app.surface,
              borderColor: app.primary,
            },
            '&:before': { borderColor: app.border },
            '&:after': { borderColor: app.primary },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          anchorOriginTopRightRectangle: {
            backgroundColor: 'red',
            color: '#FFFFFF',
          },
          badge: {
            backgroundColor: 'red !important',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          elevation1: {
            boxShadow: app.shadowSoft,
          },
          elevation4: {
            boxShadow: app.shadow,
          },
          root: {
            backgroundImage: 'none',
            backgroundColor: app.surface,
            color: app.textPrimary,
            '&-MuiDialog-paper': {
              display: 'flex',
              flex: '0,0,auto',
              padding: '8px',
              alignItems: 'center',
              justifyContent: 'flex-end',
              backgroundColor: app.surface,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            opacity: isDark ? '0.5' : '0.38',
            backgroundColor: isDark ? app.borderStrong : '#000',
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.MuiMenuItem-root.Mui-selected': {
              backgroundColor: app.primarySoft,
            },
            '&.MuiButton-root.OpenButton-textButton': {
              border: `1px solid ${alpha(app.primary, 0.45)}`,
            },
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            fontFamily: [
              '-apple-system',
              'BlinkMacSystemFont',
              '"Segoe UI"',
              'Roboto',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif',
              '"Apple Color Emoji"',
              '"Segoe UI Emoji"',
              '"Segoe UI Symbol"',
            ].join(','),
            padding: '8px 12px',
            lineHeight: '20px',
            textTransform: 'none',
            fontSize: 13.5,
            fontWeight: 650,
            borderRadius: 10,
            transition:
              'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
            '&:hover': {
              boxShadow: app.shadowSoft,
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(0,0,0,0.64)' : 'rgba(15, 23, 42, 0.28)',
            backdropFilter: 'blur(4px)',
          },
          invisible: {
            backgroundColor: 'unset',
            backdropFilter: 'none !important',
          },
        },
      },
    },

    palette: {
      mode,
      app,
      color: {
        red: app.danger,
        default: app.textPrimary,
        white: '#FFFFFF',
        whiteSmoke: app.appBg,
        status: app.primary,
        chip: '#203A4F',
        level: '#8A2BE2',
        stream: '#BE4830',
        skill: '#DC6390',
        createdAt: '#284FF0',
        updatedAt: '#854442',
        darkgray: app.textPrimary,
        gray: app.surfaceMuted,
        rounds: '#5A5AE2',
        roundStatus: '#37b4EE',
        agencyId: '#00B3B3',
      },
      background: {
        default: app.appBg,
        paper: app.surface,
        main: app.primarySoft,
        white: app.surface,
        dragBtnHover: app.surfaceMuted,
      },
      border: {
        menu: '1px solid #D3D4D5',
        donutRootDiv: '0.6px solid #7D7D7D',
        welcomeSection: '0.6px solid #7D7D7D',
        searchSuggestion: '1px solid #E0E0E0',
      },
      text: { primary: app.textPrimary, secondary: app.textSecondary, disabled: app.textMuted },
      primary: {
        main: app.primary,
        dark: app.primaryHover,
        light: app.primarySoft,
        contrastText: isDark ? '#07110f' : '#FFFFFF',
      },

      lightHighlight: { main: '#F0F0FD' },
      footer: { main: '#17173F', contrastText: 'whitesmoke' },
      quizHeader: { main: '#26225E', contrastText: 'whitesmoke' },
      errorColor: { main: app.danger },
      successColor: { main: app.primary },
      partialSuccessColor: { main: app.warning },

      primarySelection: { main: app.primary, contrastText: isDark ? '#07110f' : '#FFFFFF' },

      loader: { main: app.primary },
      header: { main: app.surfaceElevated, contrastText: app.textPrimary },

      darkTextColor: { main: app.textPrimary },
      lightTextColor: { main: '#FFFFFF' },
      grayTextColor: { main: app.textMuted },

      required: { main: app.danger },
      labelName: { main: app.textPrimary },

      lightBackground: { main: app.surface, contrastText: app.textPrimary },
      darkBackground: { main: app.appBg, contrastText: app.textPrimary },

      borders: { main: app.borderStrong },
      dividerLines: { main: app.border },
      lightDividerLines: { main: app.border },
      headerDividerLines: { main: '#383D5A' },

      highlight: { main: app.primarySoft },
      inputValidation: { main: app.danger },
      quizNotPublished: {
        text: '#dd5144',
        backgroundColor: '#FFEAEA',
      },

      primaryButton: {
        main: '#FFFFFF',
        contrastText: '#FFFFFF',
        backgroundColor: app.primary,
        border: isDark ? `1px solid ${alpha(app.primaryHover, 0.42)}` : 0,
        boxShadow: `0 8px 20px ${alpha(app.primary, isDark ? 0.16 : 0.22)}`,
        '&:hover': {
          backgroundColor: app.primaryHover,
        },
        '&:hover:disabled': {
          backgroundColor: alpha(app.primary, 0.4),
        },
        '&:active': {
          backgroundColor: app.primaryHover,
        },
        '&:disabled': {
          backgroundColor: alpha(app.primary, 0.4),
          cursor: 'not-allowed',
        },
      },

      secondaryButton: {
        main: app.textPrimary,
        backgroundColor: app.surfaceMuted,
        border: `1px solid ${app.border}`,
        '&:hover': {
          backgroundColor: app.surfaceElevated,
        },
        '&:active': {
          backgroundColor: app.surfaceMuted,
        },
      },

      warningButton: {
        main: '#FFFFFF',
        backgroundColor: app.warning,
        '&:hover': {
          backgroundColor: isDark ? '#fcd34d' : '#b45309',
        },
        '&:active': {
          backgroundColor: app.warning,
        },
      },

      deleteButton: {
        main: '#FFFFFF',
        backgroundColor: app.danger,
        '&:hover': {
          backgroundColor: app.dangerHover,
        },
        '&:hover:disabled': {
          backgroundColor: alpha(app.danger, 0.4),
        },
        '&:active': {
          backgroundColor: app.danger,
        },
        '&:disabled': {
          backgroundColor: alpha(app.danger, 0.4),
          color: '#FFF',
          cursor: 'not-allowed',
        },
      },

      confirmationButton: {
        main: '#FFFFFF',
        backgroundColor: app.primary,
        fontSize: '14px',
        fontWeight: 650,
        borderRadius: '10px',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: app.primaryHover,
          color: '#FFFFFF',
        },
      },
    },
  });
};

const theme = createAppTheme();
export default theme;
